const LONG_DATA_TYPES = new Set(['specs', 'positions', 'prices', 'stocks', 'full_sync', 'product_match']);

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isLongRunningProcessMeta(metadata = {}, processType) {
  const type = String(processType || metadata.dataType || metadata.method || '').toLowerCase();
  return Boolean(
    metadata.isLongRunning ||
    metadata.waitingForSupplier ||
    LONG_DATA_TYPES.has(type) ||
    (toFiniteNumber(metadata.totalItems) || 0) > 50000
  );
}

/**
 * Канон прогресса для UI: скачано / весь каталог.
 * Не смешивать номер батча сессии с totalItems каталога.
 * Не показывать служебные имена файлов и UUID в пользовательском слое.
 */

export function isCompletedProcessStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'completed' || s === 'success';
}
export function resolveProcessCounts(process = {}) {
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  const downloaded = toFiniteNumber(meta.downloaded ?? process.downloaded);
  const itemsProcessed = toFiniteNumber(meta.itemsProcessed ?? meta.processed ?? process.items_processed);
  const total = toFiniteNumber(meta.totalItems ?? meta.total ?? process.items_total) || 0;
  const done = downloaded != null && downloaded >= 0 ? downloaded : (itemsProcessed || 0);
  const remaining = total > 0 ? Math.max(0, total - done) : null;
  const rawPercent = total > 0
    ? (done / total) * 100
    : Number(process.displayProgress ?? process.progress_percent ?? 0);
  let percent = Math.round(rawPercent);
  if (process.status === 'completed') {
    percent = 100;
  } else if (total > 0) {
    if (done <= 0) percent = 0;
    else if (done >= total) percent = 99;
    else percent = Math.min(99, Math.max(1, percent));
  } else {
    const batch = toFiniteNumber(meta.batch);
    const totalBatches = toFiniteNumber(meta.totalBatches);
    if (batch != null && totalBatches > 0) {
      const batchPct = Math.round((batch / totalBatches) * 100);
      percent = done > 0 || batch > 0
        ? Math.min(99, Math.max(1, batchPct))
        : 0;
    } else if (String(meta.step || '') === 'initialization' && Number(process.progress_percent) === 10) {
      percent = 0;
    } else {
      percent = Math.max(0, Math.min(100, percent || 0));
    }
  }
  return { done, total, remaining, percent, rawPercent };
}

export const PROCESS_HUNG_MIN_HEARTBEAT_MS = 8 * 60 * 1000;
export const PROCESS_HUNG_LONG_HEARTBEAT_MS = 30 * 60 * 1000;
export const PROCESS_HUNG_SHORT_HEARTBEAT_MS = 15 * 60 * 1000;
export const PROCESS_HUNG_USER_LABEL = 'Нет обновления';

function toEpochMs(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * U2: свежее из lastHeartbeat / updated_at / lastProgressUpdate.
 * Не брать started_at и не верить API isStuck (там бывает duration 4ч).
 */
export function resolveProcessHeartbeatAt(process = {}) {
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  const candidates = [
    process.lastHeartbeat,
    process.updated_at,
    process.lastProgressUpdate,
    meta.lastHeartbeat,
    meta.updated_at
  ];
  let latest = null;
  for (const c of candidates) {
    const t = toEpochMs(c);
    if (t == null) continue;
    if (latest == null || t > latest) latest = t;
  }
  return latest;
}

/** Живой heartbeat важнее wall-clock duration: долгий harvest не «завис». */
export function isProcessVisiblyStuck(process = {}, now = Date.now()) {
  const status = String(process.status || '');
  if (status !== 'running' && status !== 'cancelling') return false;
  const hb = resolveProcessHeartbeatAt(process);
  if (hb == null) return false;
  const ageMs = now - hb;
  if (!Number.isFinite(ageMs) || ageMs < 0) return false;
  if (ageMs < PROCESS_HUNG_MIN_HEARTBEAT_MS) return false;
  const long = isLongRunningProcessMeta(process.metadata, process.process_type || process.metadata?.dataType);
  const limitMs = long ? PROCESS_HUNG_LONG_HEARTBEAT_MS : PROCESS_HUNG_SHORT_HEARTBEAT_MS;
  return ageMs > limitMs;
}

export function formatEtaMinutes(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 60) return `${Math.round(n)} мин`;
  const hours = Math.floor(n / 60);
  const mins = Math.round(n % 60);
  if (hours >= 48) {
    const days = Math.floor(hours / 24);
    const restH = hours % 24;
    return restH > 0 ? `${days}д ${restH}ч` : `${days}д`;
  }
  return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`;
}

/**
 * U4: один ETA. Скорость snapshot (downloaded) важнее stale estimated_duration / 1%-математики.
 * На running без snapshot ETA скрыт — не показывать 2155/2880 мин.
 */
export function resolveProcessEtaMinutes(process = {}) {
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  const fromSpeed = toFiniteNumber(meta.estimatedMinutesLeft);
  if (fromSpeed != null && fromSpeed > 0) return Math.round(fromSpeed);
  if (process.status === 'running') return null;
  const queued = toFiniteNumber(process.estimated_duration_minutes);
  if (queued != null && queued > 0) return Math.round(queued);
  return null;
}

const USER_VISIBLE_UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

export function stripUserVisibleUuid(text) {
  const raw = String(text || '');
  if (!raw) return '';
  return raw.replace(USER_VISIBLE_UUID_RE, '').replace(/\s{2,}/g, ' ').trim();
}

const OPS_JARGON_RE = /ВЭЙГПТ|WayGPT|Control Center|Fernet|DaData|jsonl|ECONNREFUSED|ETIMEDOUT|ERR_NETWORK/gi;

/** Карточка процесса / тост: без UUID, jsonl и служебного жаргона. */
export function sanitizeUserVisibleOpsText(text) {
  const stripped = stripUserVisibleUuid(text);
  if (!stripped) return '';
  return stripped
    .replace(OPS_JARGON_RE, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[,;:.\-–—\s]+|[,;:.\-–—\s]+$/g, '')
    .trim();
}

export const PROCESS_MONITOR_HOST_DOWN_COPY =
  'Сервер сейчас недоступен. Список процессов появится, когда связь восстановится.';

export function classifyProcessMonitorFetchError(err, fallback) {
  const fb = fallback || 'Не удалось загрузить список процессов. Обновите панель или страницу.';
  const code = String(err?.code || err?.cause?.code || '');
  const msg = String(err?.message || '');
  const networkish = !err?.response && (
    code === 'ECONNABORTED'
    || code === 'ETIMEDOUT'
    || code === 'ERR_NETWORK'
    || code === 'ECONNREFUSED'
    || /timeout|network|ECONNREFUSED|timed out/i.test(`${code} ${msg}`)
  );
  if (networkish) return PROCESS_MONITOR_HOST_DOWN_COPY;
  const cleaned = sanitizeUserVisibleOpsText(
    err?.response?.data?.error || err?.response?.data?.message || ''
  );
  return cleaned || fb;
}

/**
 * U7: retry только если были реальные повторы. Не «Попытка 1/3» при retry_count=0.
 */
export function resolveProcessRetry(source = {}) {
  const meta = source.metadata && typeof source.metadata === 'object' ? source.metadata : {};
  const raw = source.retry_count ?? source.retryCount ?? meta.retry_count ?? meta.retryCount;
  const countRaw = toFiniteNumber(raw);
  const count = countRaw != null && countRaw > 0 ? Math.round(countRaw) : 0;
  const maxRaw = toFiniteNumber(
    source.maxRetries ?? source.max_retries ?? meta.maxRetries ?? meta.max_retries
  );
  const max = maxRaw != null && maxRaw > 0 ? Math.round(maxRaw) : 3;
  return {
    count,
    max,
    show: count > 0,
    label: count > 0 ? `Повтор ${count} из ${max}` : null
  };
}

/**
 * U3: один блок счётчиков. «Осталось» только у ETA, не у штук.
 */
export function formatProcessedCountLine(metadata = {}, status) {
  const done = toFiniteNumber(
    metadata.downloaded ?? metadata.itemsProcessed ?? metadata.processed
  );
  const total = toFiniteNumber(metadata.totalItems ?? metadata.total);
  if (done == null) return null;
  if (total == null) {
    if (isCompletedProcessStatus(status)) {
      return {
        doneCaption: 'В базе',
        leftCaption: 'Готово',
        doneText: Number(done).toLocaleString('ru-RU'),
        totalText: null,
        leftText: '100%',
        isComplete: true
      };
    }
    return {
      doneCaption: 'Скачано',
      leftCaption: null,
      doneText: Number(done).toLocaleString('ru-RU'),
      totalText: null,
      leftText: null,
      isComplete: false
    };
  }
  const left = Math.max(0, total - done);
  if (isCompletedProcessStatus(status)) {
    return {
      doneCaption: 'В базе',
      leftCaption: 'Готово',
      doneText: Number(done).toLocaleString('ru-RU'),
      totalText: Number(total).toLocaleString('ru-RU'),
      leftText: '100%',
      isComplete: true
    };
  }
  return {
    doneCaption: 'Скачано',
    leftCaption: 'Ещё',
    doneText: Number(done).toLocaleString('ru-RU'),
    totalText: Number(total).toLocaleString('ru-RU'),
    leftText: left.toLocaleString('ru-RU'),
    isComplete: false
  };
}

/**
 * U6: ключи действий карточки. На мобиле те же ключи в меню «Действия», без обрезки кнопок.
 */
export function buildProcessCardActionKeys(status) {
  const s = String(status || '');
  const keys = [];
  if (s === 'running' || s === 'cancelling') keys.push('pause');
  if (s === 'paused') keys.push('resume');
  if (s === 'pending') keys.push('force-start');
  if (s === 'running' || s === 'pending' || s === 'paused' || s === 'cancelling') keys.push('stop');
  return keys;
}

/** U15: destructive actions must not run from a mobile Dropdown tap. */
export function processMonitorActionNeedsConfirm(key) {
  return key === 'stop' || key === 'stop-all' || key === 'force-stop';
}

export function resolveProcessMonitorConfirm(key, { status } = {}) {
  if (key === 'force-stop') {
    return {
      title: 'Принудительно остановить все процессы?',
      content: 'Все задачи будут остановлены сразу. Подтвердите, только если это нужно.',
      okText: 'Остановить',
      cancelText: 'Отмена'
    };
  }
  if (key === 'stop-all') {
    return {
      title: 'Остановить все процессы?',
      content: 'Активные загрузки будут остановлены. Подтвердите, только если это нужно.',
      okText: 'Остановить',
      cancelText: 'Отмена'
    };
  }
  if (key === 'stop') {
    const pending = String(status || '') === 'pending';
    return {
      title: pending ? 'Удалить задачу из очереди?' : 'Остановить процесс?',
      content: pending
        ? 'Задача будет снята с очереди.'
        : 'Загрузка остановится. Нажмите «Остановить» только если это нужно.',
      okText: pending ? 'Удалить' : 'Остановить',
      cancelText: 'Отмена'
    };
  }
  return null;
}

export function shouldShowBatchRow(metadata = {}) {
  const current = toFiniteNumber(metadata.currentBatch ?? metadata.batch);
  const total = toFiniteNumber(metadata.totalBatches);
  const items = toFiniteNumber(metadata.totalItems);
  const batchSize = toFiniteNumber(metadata.batchSize);
  const done = toFiniteNumber(metadata.downloaded ?? metadata.itemsProcessed);
  const dataType = String(metadata.dataType || '').toLowerCase();
  if (current == null || total == null || total <= 1) return false;
  if (current > total) return false;
  if (batchSize === 1) return false;
  if (items && total >= items * 0.5) return false;
  // 1-SKU ARM: totalBatches ≈ remaining codes at session start, not honest file batches.
  if (items && done != null && done >= 0) {
    const remaining = Math.max(0, items - done);
    const sessionStartRemaining = remaining + Math.max(0, current - 1);
    if (sessionStartRemaining > 50) {
      const delta = Math.abs(total - sessionStartRemaining) / sessionStartRemaining;
      if (delta < 0.25) return false;
    }
  }
  if (dataType === 'specs' && total > 20 && (batchSize == null || batchSize <= 1)) return false;
  return true;
}

const ACTIVE_PROCESS_STATUSES = new Set(['pending', 'running', 'paused']);

/**
 * U9: бейдж шапки = тот же catalog % что drawer, не stale progress_percent и не «0» при downloaded>0.
 * Несколько активных без одного running-каталога → счётчик процессов.
 */
export function resolveHeaderProcessBadge(processes = []) {
  const list = Array.isArray(processes) ? processes : [];
  const active = list.filter((p) => ACTIVE_PROCESS_STATUSES.has(p?.status));
  const count = active.length;
  if (count === 0) {
    return {
      count: 0,
      text: null,
      percent: null,
      kind: 'empty',
      tooltip: 'Процессы'
    };
  }

  const running = active.filter((p) => p.status === 'running');
  if (running.length === 1) {
    const process = running[0];
    const counts = resolveProcessCounts(process);
    if (counts.total > 0 || counts.done > 0) {
      const queued = Math.max(0, count - 1);
      const name = String(process.process_name || '').trim();
      const tooltip = queued > 0
        ? `${name || 'Загрузка'}: ${counts.percent}% · ещё ${queued} в очереди`
        : `${name || 'Загрузка'}: ${counts.percent}%`;
      return {
        count,
        text: `${counts.percent}%`,
        percent: counts.percent,
        kind: 'percent',
        tooltip
      };
    }
  }

  return {
    count,
    text: count > 99 ? '99+' : String(count),
    percent: null,
    kind: 'count',
    tooltip: count === 1 ? '1 процесс' : `Процессы: ${count}`
  };
}

const PROCESS_KIND_LABELS = {
  positions: 'Позиции',
  prices: 'Цены',
  stocks: 'Остатки',
  specs: 'Характеристики',
  full_sync: 'Полная синхронизация',
  catalog_campaign: 'Каталог',
  price_recalculation: 'Цены',
  product_match: 'Сопоставление',
};

const PROCESS_KIND_TITLE_HINTS = {
  positions: ['позици'],
  prices: ['цен'],
  stocks: ['остатк'],
  specs: ['характеристик'],
  full_sync: ['полная синхронизац', 'полная синхр']
};

function processKindKey(process = {}) {
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  return String(meta.dataType || meta.method || process.process_type || '').toLowerCase();
}

/**
 * U10: человеческий тип процесса без leftover «Характеристики 0» и без дубля заголовка.
 * Счётчик живёт в progress bar (`resolveProcessCounts`), не в теге.
 */
export function resolveProcessKindTag(process = {}) {
  const key = processKindKey(process);
  const label = PROCESS_KIND_LABELS[key];
  if (!label) {
    return { show: false, text: null };
  }
  const title = String(
    process.process_name || process.operation_name || process.operation_type || ''
  ).toLowerCase();
  const hints = PROCESS_KIND_TITLE_HINTS[key] || [label.toLowerCase()];
  if (hints.some((hint) => title.includes(hint))) {
    return { show: false, text: null };
  }
  return { show: true, text: label };
}

/**
 * U5: trail прогресс-бара должен быть виден на карточке.
 * Запрещён trail = colorBgContainer (белый на белом → бар «пропадает» при 0%).
 * colorFillSecondary часто почти совпадает с контейнером — берём более плотный fill/border.
 */
export function resolveProgressTrailColor(token = {}) {
  const container = token.colorBgContainer ? String(token.colorBgContainer).toLowerCase() : null;
  const candidates = [
    token.colorFill,
    token.colorBorderSecondary,
    token.colorFillTertiary,
    token.colorFillSecondary,
    token.colorBorder,
    token.colorTextQuaternary
  ].filter(Boolean);
  for (const candidate of candidates) {
    if (!container || String(candidate).toLowerCase() !== container) {
      return candidate;
    }
  }
  return token.colorTextSecondary || token.colorFill || token.colorBorderSecondary || null;
}

export const PROCESS_SLOT_BACKPRESSURE_TITLE = 'Слоты: счётчик временно недоступен';
export const PROCESS_SLOT_BACKPRESSURE_HINT =
  'Система занята обработкой. Это не зависание загрузки и не исчерпание тарифа. Процессы продолжают работать — обновите панель через минуту.';

/**
 * U8: слоты getResourceUsage. backpressure ≠ hung (U2) и ≠ «лимит тарифа исчерпан».
 * Не показывать «н/д (нагрузка БД)» — оператор путает с зависанием harvest.
 */
export function resolveProcessSlotUsage(usage = {}, queueLimits = null) {
  const src = usage && typeof usage === 'object' ? usage : {};
  const backpressure = Boolean(src.backpressure);
  if (backpressure) {
    const tariff = queueLimits?.max_concurrent_processes ?? null;
    return {
      backpressure: true,
      title: PROCESS_SLOT_BACKPRESSURE_TITLE,
      tariffNote: tariff != null && tariff !== '' ? `Лимит тарифа: ${tariff}` : null,
      hint: PROCESS_SLOT_BACKPRESSURE_HINT,
      showProgress: false,
      showRemaining: false
    };
  }
  const current = src.currentProcesses ?? 0;
  const unlimited = Boolean(src.unlimited);
  const maxLabel = unlimited ? '∞' : (src.maxProcesses ?? '—');
  return {
    backpressure: false,
    title: `Слоты: ${current} из ${maxLabel}`,
    tariffNote: null,
    hint: null,
    showProgress: !unlimited && src.maxProcesses != null,
    showRemaining: src.remainingProcesses !== null && src.remainingProcesses !== undefined && !unlimited
  };
}

const INGEST_DATA_TYPES = new Set(['prices', 'stocks']);

function ingestKindFromProcess(process = {}) {
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  const type = String(meta.dataType || meta.method || process.process_type || '').toLowerCase();
  if (type === 'prices') return 'prices';
  if (type === 'stocks') return 'stocks';
  const name = String(process.process_name || '').toLowerCase();
  if (name.includes('цен')) return 'prices';
  if (name.includes('остатк')) return 'stocks';
  return null;
}

export function isSupplierIngestProcess(process = {}) {
  return ingestKindFromProcess(process) != null;
}

export function processMatchesSupplier(process = {}, supplierId) {
  if (!supplierId) return true;
  const meta = process.metadata && typeof process.metadata === 'object' ? process.metadata : {};
  const candidates = [
    meta.supplierId,
    meta.supplier_id,
    process.supplier_id,
    process.supplierId
  ].filter((value) => value != null && String(value).trim() !== '');
  if (candidates.length === 0) return true;
  const sid = String(supplierId);
  return candidates.some((value) => String(value) === sid);
}

export function pickSupplierIngestProcesses(processes = [], { supplierId } = {}) {
  return (Array.isArray(processes) ? processes : [])
    .filter((process) => ACTIVE_PROCESS_STATUSES.has(process?.status))
    .filter((process) => isSupplierIngestProcess(process))
    .filter((process) => processMatchesSupplier(process, supplierId));
}

/**
 * Человеческая строка прогресса цен/остатков на /products.
 * Без UUID, без имён файлов, без batch/totalBatches как «каталог».
 */
export function formatSupplierIngestBanner(processes = []) {
  const list = Array.isArray(processes) ? processes : [];
  if (list.length === 0) return null;
  const running = list.find((process) => process.status === 'running') || list[0];
  const counts = resolveProcessCounts(running);
  const kind = ingestKindFromProcess(running);
  const kindLabel = kind === 'stocks' ? 'остатков' : 'цен';
  const queuedKinds = list
    .filter((process) => process.status === 'pending')
    .map((process) => ingestKindFromProcess(process))
    .filter(Boolean);
  let queuedNote = null;
  if (queuedKinds.includes('stocks')) queuedNote = 'Остатки ждут своей очереди.';
  else if (queuedKinds.includes('prices')) queuedNote = 'Цены ждут своей очереди.';
  else if (queuedKinds.length > 0) queuedNote = 'Следующий этап в очереди.';
  const title = running.status === 'pending'
    ? (kind === 'stocks' ? 'Остатки в очереди' : 'Цены в очереди')
    : `Идёт загрузка ${kindLabel}`;
  let countLine;
  if (counts.total > 0) {
    countLine = `${Number(counts.done).toLocaleString('ru-RU')} из ${Number(counts.total).toLocaleString('ru-RU')} · ${counts.percent}%`;
  } else if (counts.done > 0) {
    countLine = `Скачано ${Number(counts.done).toLocaleString('ru-RU')} · ${counts.percent}%`;
  } else if (running.status === 'pending') {
    countLine = 'Ожидает очереди';
  } else {
    countLine = 'Считаем каталог…';
  }
  const downloading = running.status === 'running' && String(running.metadata?.step || '').startsWith('download');
  const hint = downloading || (running.status === 'running' && kind === 'prices')
    ? 'Сейчас скачиваем прайс. В учёт и на витрину цифры попадут после окончания загрузки, принятия и публикации. Не запускайте загрузку повторно.'
    : 'Цифры — сколько позиций уже скачано из каталога поставщика. В витрину они попадут после принятия и публикации.';
  return {
    title,
    countLine,
    queuedNote,
    percent: counts.percent,
    status: running.status,
    hint
  };
}

export const SUPPLIER_INGEST_BUSY_MESSAGE =
  'Уже идёт загрузка данных поставщика. Дождитесь окончания — повторный запуск перегрузит API и остановит текущую загрузку.';

/**
 * Запрет второй загрузки этого поставщика, пока цены/остатки running/pending.
 * Характеристики и позиции в том же окне тоже бьют общий лимит API.
 */
export function supplierIngestBlocksDownload(processes = [], selectedTypes = [], supplierId = null) {
  const ingest = pickSupplierIngestProcesses(processes, { supplierId });
  const busy = ingest.some((process) => process.status === 'running' || process.status === 'pending');
  if (!busy) return null;
  const types = Array.isArray(selectedTypes) ? selectedTypes : [];
  const wantsSupplierApi = types.some((type) => ['prices', 'stocks', 'positions', 'specs'].includes(String(type)));
  if (types.length > 0 && !wantsSupplierApi) return null;
  return SUPPLIER_INGEST_BUSY_MESSAGE;
}
